import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { Citizenship } from "../../Dashboard/constants/citizenship";
import { AdvisorId, ClientId, Prefix, Suffix, } from "../../Types";
import ValidationError from "../../ZodError";
export const Name = z.object({
    first: z.string().describe("The first name of the client"),
    last: z.string().describe("The last name of the client"),
    middle: z.string().nullable().describe("The middle name of the client"),
    prefix: Prefix.nullable().describe("The prefix of the client's name"),
    suffix: Suffix.nullable().describe("The suffix of the client's name"),
});
export const genderOptions = [
    "Male",
    "Female",
    "Non-binary",
    "Undisclosed",
];
export const GenderSchema = z.enum(genderOptions);
export const martialStatusOptions = ["Married", "Single"];
export const MaritalStatusSchema = z.enum(martialStatusOptions);
export const IncomeRangeOptions = [
    "Under150Thousand",
    "Under250Thousand",
    "Under500Thousand",
    "Under1Million",
    "Over1Million",
];
export const IncomeRangeSchema = z.enum(IncomeRangeOptions);
export const AddressSchema = z.object({
    street: z.string().describe("The first line of the address"),
    city: z.string().describe("The city of the address"),
    state: z.string().length(2).describe("The state of the address"),
    postalCode: z.string().length(5).describe("The zip code of the address"),
    country: z.string().length(2).describe("The country of the address"),
});
export const EmailAddressSchema = z.string().brand("EmailAddress");
export const PhoneNumberSchema = z.string().brand("PhoneNumber");
export const PersonIdSchema = z.string().brand("PersonId");
export const employmentStatusOptions = [
    "Employed",
    "Unemployed",
    "Retired",
];
export const EmploymentStatusSchema = z.enum(employmentStatusOptions);
export const ClientEmployerDetailsSchema = z.object({
    occupation: z.string().describe("The occupation of the client"),
    startYear: z
        .number()
        .int()
        .min(1900)
        .max(new Date().getFullYear() + 5)
        .describe("The year the client started their current occupation"),
    employerName: z.string().min(1).max(40).describe("Name of client's employer"),
    employerAddress: AddressSchema.describe("The address of the client's employer"),
});
export const investorTypeOptions = [
    "Conservative",
    "Moderate",
    "Aggressive",
];
export const InvestorTypeSchema = z.enum(investorTypeOptions);
export const investingObjectiveOptions = [
    "Maximizing gains",
    "Minimizing losses",
    "Both equally",
];
export const InvestingObjectiveSchema = z.enum(investingObjectiveOptions);
export const TaxIdType = z.enum(["SSN", "ITIN", "EIN", "Applied For"]);
export const ClientInfoSchema = z.object({
    clientId: ClientId.describe("The Person Id of the client"),
    advisorId: AdvisorId.describe("The Person Id of the client's advisor"),
    contactInformation: z.object({
        emailAddress: EmailAddressSchema.describe("The email of the client"),
        phoneNumber: PhoneNumberSchema.nullable().describe("The new phone number of the client"),
    }),
    name: Name.nullable().describe("The new name of the client"),
    gender: GenderSchema.nullable().describe("The gender of the client"),
    maritalStatus: MaritalStatusSchema.nullable().describe("The marital status of the client"),
    dateOfBirth: z
        .string()
        .nullable()
        .describe("The date of birth of the client, format: yyyy-mm-dd"),
    citizenship: Citizenship,
    taxInformation: z
        .object({
        taxId: z.string().describe("Either the TIN or SSN of the client"),
        taxIdType: TaxIdType,
    })
        .nullable()
        .describe("The tax information of the client"),
    incomeRange: IncomeRangeSchema.nullable().describe("The income range of the client"),
    estimatedNetWorthMillions: z
        .number()
        .min(0, "Must be greater than or equal to 0")
        .nullable()
        .describe("The estimated net worth of the client. 1 = 1,000,000"),
    address: AddressSchema.nullable().describe("The address of the client"),
    employmentDetails: z
        .object({
        employmentStatus: EmploymentStatusSchema.nullable().describe("The employment status of the client"),
        employer: ClientEmployerDetailsSchema.nullable().describe("The employer of the client"),
    })
        .describe("Employment details of the client"),
    taxBracketInfo: z
        .object({
        taxBracketLTG: z
            .number()
            .int()
            .nullable()
            .describe("The tax bracket of the client for long term gains"),
        taxBracketOrdinary: z
            .number()
            .int()
            .nullable()
            .describe("The tax bracket of the client for ordinary income"),
        capitalLossCarryover: z
            .number()
            .nullable()
            .describe("The capital loss carryover of the client"),
    })
        .describe("Tax information of the client"),
    isAccreditedInvestor: z
        .boolean()
        .nullable()
        .describe("Whether the client is an accredited investor"),
    investorType: InvestorTypeSchema.nullable().describe("The investor type of the client"),
    investingObjective: InvestingObjectiveSchema.nullable().describe("The investing objective of the client"),
    iraOutsideFarther: z
        .number()
        .min(0)
        .nullable()
        .describe("Any IRA contributions outside Farther"),
});
export const url = (clientId) => environment() === "PROD"
    ? `https://middleware.prod.farther.com/entity/client/${clientId}`
    : `https://middleware.uat.farther.com/entity/client/${clientId}`;
const getClient = async (clientId, config) => {
    const { data } = await axios.get(url(clientId), {
        headers: {
            Client: config.clientId,
            Authorization: `Bearer ${config.jwt}`,
            ...(config.advisorId && { Advisor: config.advisorId }),
            version: "20240626",
        },
    });
    const decoded = ClientInfoSchema.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = "getClient returned unexpected data.";
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
};
export default getClient;
//# sourceMappingURL=getClient.js.map